import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getBaseUrl } from '../../config/api';
import { WEB_APP_ORIGIN } from '../storeConfig';
import DeepLinkShell from '../DeepLinkShell';
import SmartAppCta from '../SmartAppCta';

interface StylistPreview {
  _id: string;
  name?: string;
  businessName?: string;
  specialties?: string[];
  profilePhoto?: { url?: string };
  bio?: string | { shortBio?: string; fullBio?: string };
  location?: { city?: string; state?: string };
  metrics?: { ratings?: { average?: number; count?: number } };
}

function bioText(bio: StylistPreview['bio']): string {
  if (!bio) return '';
  if (typeof bio === 'string') return bio;
  return bio.shortBio || bio.fullBio || '';
}

const StylistLanderPage: React.FC = () => {
  const { shortCode: id } = useParams<{ shortCode: string }>();
  const [stylist, setStylist] = useState<StylistPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!id) {
        setError('Missing stylist');
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${getBaseUrl()}/profile/stylists/${encodeURIComponent(id)}`);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.message || 'Stylist not found');
        }
        if (!cancelled) {
          setStylist(data.data || data);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Unable to load stylist');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const name = stylist?.name || stylist?.businessName || 'Stylist';
  const initials = name.charAt(0).toUpperCase();
  const location =
    stylist?.location?.city && stylist?.location?.state
      ? `${stylist.location.city}, ${stylist.location.state}`
      : '';
  const specialties = (stylist?.specialties || []).slice(0, 5);
  const rating = stylist?.metrics?.ratings?.average;
  const reviewCount = stylist?.metrics?.ratings?.count;

  return (
    <DeepLinkShell>
      <div className="dl-card">
        {loading && <p className="dl-loading">Loading stylist…</p>}
        {error && !loading && <p className="dl-error">{error}</p>}
        {!loading && !error && stylist && (
          <>
            {stylist.profilePhoto?.url ? (
              <img className="dl-avatar" src={stylist.profilePhoto.url} alt={name} />
            ) : (
              <div className="dl-avatar dl-avatar--initials" aria-hidden>
                {initials}
              </div>
            )}
            <h1 className="dl-name">{name}</h1>
            <p className="dl-meta">
              {[location, rating ? `${rating.toFixed(1)}★ (${reviewCount || 0})` : null]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {bioText(stylist.bio) && (
              <p className="dl-meta">{bioText(stylist.bio).slice(0, 180)}</p>
            )}
            {specialties.length > 0 && (
              <div className="dl-tags">
                {specialties.map((s) => (
                  <span key={s} className="dl-tag">
                    {s}
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        <SmartAppCta
          title={`Book with ${name} on LookBel`}
          subtitle="Open the app to view availability and book, or download LookBel to get started."
          webHref={`${WEB_APP_ORIGIN}/login?redirectTo=${encodeURIComponent(`/client/stylist/${id}`)}`}
          webLabel="Sign in on the web"
        />
      </div>
    </DeepLinkShell>
  );
};

export default StylistLanderPage;
